import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const navLinks = [
  { label: "Home", icon: "ri-home-4-line" },
  { label: "About", icon: "ri-user-line" },
  { label: "Services", icon: "ri-service-line" },
  { label: "Projects", icon: "ri-folder-3-line" },
  { label: "Skills", icon: "ri-code-s-slash-line" },
  { label: "Education", icon: "ri-graduation-cap-line" },
  { label: "Contact", icon: "ri-mail-line" },
];

export default function DevNavbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("Home");
  const overlayRef = useRef(null);
  const itemRefs = useRef([]);
  const curtainRef = useRef(null);
  const gsapRef = useRef(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js";
    script.onload = () => { gsapRef.current = window.gsap; };
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  // Portfolio typography: Space Grotesk (headings/labels) + JetBrains Mono (accents)
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    const gsap = gsapRef.current;
    if (!gsap || !overlayRef.current) return;

    if (menuOpen) {
      gsap.set(overlayRef.current, { display: "flex" });
      gsap.fromTo(curtainRef.current,
        { scaleY: 0, transformOrigin: "top center" },
        { scaleY: 1, duration: 0.45, ease: "expo.inOut" }
      );
      gsap.fromTo(overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.25, ease: "power2.out" }
      );
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, delay: 0.18 + i * 0.055, ease: "power3.out" }
        );
      });
    } else {
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.to(el, { y: -16, opacity: 0, duration: 0.2, delay: i * 0.025, ease: "power2.in" });
      });
      gsap.to(curtainRef.current, {
        scaleY: 0, transformOrigin: "bottom center",
        duration: 0.35, delay: 0.12, ease: "expo.inOut",
      });
      gsap.to(overlayRef.current, {
        opacity: 0, duration: 0.3, delay: 0.25, ease: "power2.in",
        onComplete: () => { if (overlayRef.current) overlayRef.current.style.display = "none"; },
      });
    }
  }, [menuOpen]);

  const scrollTo = (id) => {
    const wasOpen = menuOpen;
    setMenuOpen(false);
    setActive(id);
    const delay = wasOpen ? 380 : 0;
    setTimeout(() => {
      const el = document.getElementById(id.toLowerCase());
      if (!el) return;
      const navHeight = 64;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }, delay);
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? "rgba(8,8,8,0.94)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-8 flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <button
            onClick={() => scrollTo("Home")}
            className="text-lg tracking-tight cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}
          >
            <span className="text-white">HR </span>
            <span style={{ color: "#86efac", opacity: 0.8 }}>Safin</span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ label }) => (
              <button
                key={label}
                onClick={() => scrollTo(label)}
                className={`relative px-3 py-1.5 text-[13px] transition-all duration-200 cursor-pointer whitespace-nowrap rounded-md flex items-center gap-1.5 ${
                  active === label ? "text-gray-200" : "text-gray-600 hover:text-gray-300"
                }`}
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, letterSpacing: "0.01em" }}
              >
                {active === label && (
                  <span className="absolute inset-0 rounded-md" style={{ background: "rgba(255,255,255,0.05)" }} />
                )}
                <span className="relative">{label}</span>
              </button>
            ))}

            <button
              onClick={() => scrollTo("Contact")}
              className="relative ml-3 px-5 py-2 rounded-full text-[13px] whitespace-nowrap cursor-pointer flex items-center gap-1.5 overflow-hidden"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                letterSpacing: "0.01em",
                color: "#052e10",
                background: "linear-gradient(180deg, #6ee7a3 0%, #4ade80 35%, #16a34a 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 3px rgba(21,94,53,0.35), 0 3px 0 #15803d, 0 8px 16px -4px rgba(22,163,74,0.5)",
                transform: "translateY(0)",
                transition: "transform 0.12s ease, box-shadow 0.12s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 3px rgba(21,94,53,0.35), 0 4px 0 #15803d, 0 10px 18px -4px rgba(22,163,74,0.55)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 3px rgba(21,94,53,0.35), 0 3px 0 #15803d, 0 8px 16px -4px rgba(22,163,74,0.5)";
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "translateY(3px)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 2px rgba(21,94,53,0.3), 0 0px 0 #15803d, 0 2px 6px -2px rgba(22,163,74,0.4)";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 3px rgba(21,94,53,0.35), 0 4px 0 #15803d, 0 10px 18px -4px rgba(22,163,74,0.55)";
              }}
            >
              <i className="ri-briefcase-line text-xs" />
              Hire Me
            </button>
          </nav>

          {/* Hamburger */}
          <button
            className="md:hidden relative w-8 h-8 flex flex-col items-center justify-center gap-[5px] cursor-pointer z-[60]"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className="block h-[2px] rounded-full transition-all duration-300 origin-center"
              style={{
                width: "22px",
                background: menuOpen ? "#86efac" : "#6b7280",
                transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none",
              }}
            />
            <span
              className="block h-[2px] rounded-full transition-all duration-300"
              style={{
                width: menuOpen ? "0px" : "14px",
                background: "#6b7280",
                opacity: menuOpen ? 0 : 1,
                alignSelf: "flex-start",
              }}
            />
            <span
              className="block h-[2px] rounded-full transition-all duration-300 origin-center"
              style={{
                width: "22px",
                background: menuOpen ? "#86efac" : "#6b7280",
                transform: menuOpen ? "rotate(-45deg) translateY(-7px)" : "none",
              }}
            />
          </button>
        </div>
      </header>

      {/* Full-screen mobile menu */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-40 md:hidden flex items-center justify-center"
        style={{ display: "none", opacity: 0 }}
      >
        {/* Pure black curtain */}
        <div
          ref={curtainRef}
          className="absolute inset-0"
          style={{ background: "#080808" }}
        />

        {/* Noise texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E\")",
            backgroundSize: "180px 180px",
          }}
        />

        {/* Ambient glow top-left */}
        <div
          className="absolute top-0 left-0 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(22,163,74,0.06) 0%, transparent 70%)",
            filter: "blur(60px)",
            transform: "translate(-30%, -30%)",
          }}
        />
        {/* Ambient glow bottom-right */}
        <div
          className="absolute bottom-0 right-0 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(22,163,74,0.04) 0%, transparent 70%)",
            filter: "blur(60px)",
            transform: "translate(30%, 30%)",
          }}
        />

        {/* Center card */}
        <nav
          className="relative z-10 flex flex-col items-center justify-center w-full"
          style={{ gap: "6px", padding: "0 32px" }}
        >
          {/* Fixed-width menu block — all items same width, icons aligned */}
          <div className="w-full" style={{ maxWidth: "260px" }}>
            {navLinks.map(({ label, icon }, idx) => (
              <button
                key={label}
                ref={(el) => { itemRefs.current[idx] = el; }}
                onClick={() => scrollTo(label)}
                className="w-full cursor-pointer group mb-1.5"
                style={{ opacity: 0, display: "block" }}
              >
                <div
                  className="relative flex items-center gap-3 py-3 px-4 rounded-2xl transition-all duration-300"
                  style={{
                    background: active === label
                      ? "rgba(22,163,74,0.08)"
                      : "rgba(255,255,255,0.025)",
                    border: active === label
                      ? "1px solid rgba(22,163,74,0.25)"
                      : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {/* Active left accent line */}
                  {active === label && (
                    <div
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-full"
                      style={{ background: "rgba(22,163,74,0.7)" }}
                    />
                  )}

                  {/* Icon box — fixed size so all icons align */}
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{
                      background: active === label
                        ? "rgba(22,163,74,0.15)"
                        : "rgba(255,255,255,0.04)",
                      border: active === label
                        ? "1px solid rgba(22,163,74,0.3)"
                        : "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <i
                      className={`${icon} text-base`}
                      style={{
                        color: active === label ? "#86efac" : "rgba(156,163,175,0.5)",
                        filter: active === label ? "brightness(0.8) saturate(0.75)" : "none",
                      }}
                    />
                  </div>

                  {/* Label */}
                  <span
                    className="text-sm tracking-wide"
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 600,
                      color: active === label ? "#f3f4f6" : "rgba(156,163,175,0.55)",
                    }}
                  >
                    {label}
                  </span>
                </div>
              </button>
            ))}

            {/* Hire Me CTA */}
            <button
              onClick={() => scrollTo("Contact")}
              className="w-full mt-3 py-3.5 rounded-2xl text-sm tracking-wide cursor-pointer flex items-center justify-center gap-2"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                color: "#052e13",
                background: "linear-gradient(180deg, #6ee7a3 0%, #4ade80 35%, #16a34a 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 3px rgba(21,94,53,0.35), 0 4px 0 #15803d, 0 10px 20px -4px rgba(22,163,74,0.5)",
                transform: "translateY(0)",
                transition: "transform 0.12s ease, box-shadow 0.12s ease",
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = "translateY(3px)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 2px rgba(21,94,53,0.3), 0 0px 0 #15803d, 0 3px 8px -2px rgba(22,163,74,0.4)";
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 3px rgba(21,94,53,0.35), 0 4px 0 #15803d, 0 10px 20px -4px rgba(22,163,74,0.5)";
              }}
            >
              <i className="ri-briefcase-line text-sm" />
              Hire Me
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}